import React, { Component, createRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import shaka from 'shaka-player';
import muxjs from 'mux.js';
import { find, isEmpty, uniqBy } from 'lodash';
import VIDEO_EVENTS from 'constants/video-events';
import AbstractPlayerInterface from './HTML5PlayerInterface';
import VideoComponent from './VideoComponent';
import PLAYER_TYPES from '../../constants/player-types';
import urlHelper from '../../helpers/urlhelper';
import api from '../../config/api';
import requester from '../../utils/requester';
import Platform from 'platform';

const ABR_DISABLE = { abr: { enabled: false } };
const ABR_ENABLE = {
  abr: {
    enabled: true,
    // Не выбирать вариант близко к текущему bandwidth estimate
    bandwidthUpgradeTarget: 0.75,
    bandwidthDowngradeTarget: 0.95,
    // Ограничить скорость частых переключений
    switchInterval: 8,
  },
};

const STREAMING_CONFIG = {
  streaming: {
    gapDetectionThreshold: 0.5,
    stallSkip: 0,
    rebufferingGoal: 4,
    segmentPrefetchLimit: 0,
    gapPadding: 2,
    shouldFixTimestampOffset: true,
    inaccurateManifestTolerance: 4,
    returnToEndOfLiveWindowWhenOutside: true, // только CHANNEL
  },
};

class ShakaPlayer extends Component {
  videoRef = createRef();
  abstractPlayerInterfaceRef = this.props.forwardedRef || createRef();
  shakaVideo = null;
  tickerTimerID = null;
  deviceUID = Platform.getUID();

  // Protects against async race conditions:
  // - old loadManifestSettings() resolving after src/page change
  // - delayed onPlayerOpen() running after unmount
  // - old Shaka instance continuing to update MPD in background
  isUnmounted = false;
  openId = 0;

  UNSAFE_componentWillMount() {
    window.muxjs = muxjs;
    shaka.polyfill.installAll();
  }

  componentDidMount() {
    this.isUnmounted = false;
    this.abstractPlayerInterfaceRef.current = new AbstractPlayerInterface(this.videoRef);
    this.onPlayerOpen();
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.openId += 1;

    clearTimeout(this.tickerTimerID);
    this.tickerTimerID = null;

    this.destroyPlayer();

    this.abstractPlayerInterfaceRef.current = null;
  }

  componentDidUpdate(prevProps) {
    const isNewSrc = prevProps.src !== this.props.src;
    const isNewContentId = prevProps.contentId !== this.props.contentId;
    const isNewLang = prevProps.lang !== this.props.lang;
    const isNewQuality = prevProps.quality !== this.props.quality;

    if (isNewSrc || isNewContentId) this.onChangeSrc();
    if (isNewLang) this.onChangeLang();
    if (isNewQuality) this.onChangeQuality();
  }

  async onChangeSrc() {
    clearTimeout(this.tickerTimerID);
    this.tickerTimerID = null;

    // Invalidate any currently running onPlayerOpen/loadManifestSettings/load.
    this.openId += 1;

    await this.destroyPlayer();

    if (this.isUnmounted) return;

    this.tickerTimerID = setTimeout(() => {
      this.tickerTimerID = null;

      if (this.isUnmounted) return;

      this.onPlayerOpen();
    }, 500);
  }

  onChangeLang() {
    const { lang } = this.props;
    this.shakaVideo && this.shakaVideo.selectAudioLanguage(lang);
  }

  onChangeQuality() {
    if (!this.shakaVideo) return;

    const { quality } = this.props;
    const tracks = this.shakaVideo.getVariantTracks();
    const currentTrack = find(tracks, { active: true });

    if (!currentTrack) return;

    const chosenTrack = find(tracks, {
      language: currentTrack.language,
      videoBandwidth: quality,
    });

    if (!chosenTrack) {
      console.warn('[Shaka] quality track not found', {
        quality,
        currentTrack,
        tracks,
      });
      return;
    }

    this.shakaVideo.configure(ABR_DISABLE);

    // Normal user quality change: do not clear buffer.
    // Emergency buffer clear is done only in recoverByReselectingVariant().
    this.shakaVideo.selectVariantTrack(chosenTrack, false);
  }

  async loadManifestSettings() {
    const { contentId, contentType, isLoggedIn, isDRMSupported } = this.props;
    let urls = [];

    if (contentType === 'channel') {
      const endpointUrlChannelHLS = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.channelDetailsUrl(contentId, 'hlsFree'),
        isLoggedIn,
      );
      const endpointUrlChannel = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.channelDetailsUrl(contentId, 'dashWv'),
        isLoggedIn,
      );
      if (isDRMSupported) {
        urls.push(`${api.host}${endpointUrlChannel}`);
      } else {
        urls.push(`${api.host}${endpointUrlChannelHLS}`);
      }
    } else if (contentType === 'epg') {
      const endpointUrlCatchUpHLS = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.epgDetailsUrl(contentId, 'hlsFree'),
        isLoggedIn,
      );
      const endpointUrlCatchUp = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.epgDetailsUrl(contentId, 'dashWv'),
        isLoggedIn,
      );
      if (isDRMSupported) {
        urls.push(`${api.host}${endpointUrlCatchUp}`);
      } else {
        urls.push(`${api.host}${endpointUrlCatchUpHLS}`);
      }
    } else {
      const endpointUrlVOD_HLSFree = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.movieDetailsUrl(contentId, 'hlsFree'),
        isLoggedIn,
      );
      const endpointUrlVOD_DashWv = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.movieDetailsUrl(contentId, 'dashWv'),
        isLoggedIn,
      );
      const endpointUrlVOD_DashFree = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.movieDetailsUrl(contentId, 'dashFree'),
        isLoggedIn,
      );

      if (isDRMSupported) {
        const drmManifestData = await requester({
          method: 'get',
          url: `${api.host}${endpointUrlVOD_DashWv}`,
        });

        if (!isEmpty(drmManifestData.contentSettings)) return drmManifestData;

        //ЕСЛИ DRM пришёл пустым пробуем найти без ДРМные настройки в других профилях
        urls.push(`${api.host}${endpointUrlVOD_DashFree}`);
        urls.push(`${api.host}${endpointUrlVOD_HLSFree}`);

        const manifests = await Promise.all(urls.map((url) => requester({ method: 'get', url })));
        return find(manifests, (data) => !isEmpty(data.contentSettings)) || manifests[0];
      }

      // Логика для устройств без поддержки DRM
      urls.push(`${api.host}${endpointUrlVOD_HLSFree}`);
      urls.push(`${api.host}${endpointUrlVOD_DashFree}`);
    }

    const manifests = await Promise.all(urls.map((url) => requester({ method: 'get', url })));
    return find(manifests, (data) => !isEmpty(data.contentSettings)) || manifests[0];
  }

  isStaleOpen(currentOpenId, player) {
    return this.isUnmounted || currentOpenId !== this.openId || this.shakaVideo !== player;
  }

  getPreferredAudioLanguage(contentSettings = []) {
    const priority = ['uk', 'ru', 'en'];

    const availableLanguages = contentSettings
      .map((track) => track.language?.toLowerCase())
      .filter(Boolean)
      .map((language) => {
        if (language === 'ua') return 'uk';
        return language;
      });

    return priority.find((language) => availableLanguages.includes(language));
  }

  async onPlayerOpen() {
    if (this.isUnmounted) return;

    const currentOpenId = ++this.openId;

    const { verimatrixId, playerType } = this.props;
    const { onPlayerOpen, onSettingsChange, onVideoEvent } = this.props;

    const data = await this.loadManifestSettings();

    if (this.isUnmounted || currentOpenId !== this.openId) return;

    const preferredAudioLanguage = this.getPreferredAudioLanguage(data?.contentSettings);
    const contentUrl = data.contentUrl;

    if (this.isUnmounted || currentOpenId !== this.openId) return;
    if (!contentUrl) return;

    const video = this.videoRef.current;

    if (!video) return;

    let securedSrc = contentUrl.replace('http://', 'https://');

    if (securedSrc && [PLAYER_TYPES.CHANNEL, PLAYER_TYPES.CATCHUP, PLAYER_TYPES.TIMESHIFT].includes(playerType)) {
      const divider = securedSrc.includes('?') ? '&' : '?';
      securedSrc += `${divider}did=${this.deviceUID}`;
    }

    const deviceId = `?deviceId=${verimatrixId}`;

    const startPosition = this.getStartPosition();

    const player = new shaka.Player(video);
    this.shakaVideo = player;

    // В onPlayerOpen, после создания this.shakaVideo:
    shaka.log?.setLevel(shaka.log.Level.DEBUG); // видеть внутренние логи Shaka

    player.addEventListener('error', (e) => {
      console.error('[Shaka] error event:', e.detail);
    });

    player.addEventListener('stalldetected', () => {
      console.warn('[Shaka] stall detected');
    });

    player.configure({
      preferredAudioLanguage,
      drm: {
        servers: {
          'com.widevine.alpha': 'https://drm.ott.volia.com:8063' + deviceId,
          'com.microsoft.playready': 'https://drm.ott.volia.com:8062/PlayReady/rightsmanager.asmx' + deviceId,
        },
      },
    });

    const isLiveLike = [
      PLAYER_TYPES.CHANNEL,
      PLAYER_TYPES.TIMESHIFT,
      PLAYER_TYPES.CATCHUP,
      PLAYER_TYPES.VOD_CHANNEL,
    ].includes(playerType);

    if (isLiveLike) {
      player.configure(ABR_DISABLE);
    } else {
      player.configure(ABR_ENABLE);
    }

    player.configure(STREAMING_CONFIG);

    this.shakaVideo.addEventListener('adaptation', () => onSettingsChange(this.getAvailableSettings()));

    this.shakaVideo.addEventListener('variantchanged', () => onSettingsChange(this.getAvailableSettings()));

    onPlayerOpen();

    try {
      if (typeof startPosition === 'number') {
        await player.load(securedSrc, startPosition);
      } else {
        await player.load(securedSrc);
      }

      if (this.isStaleOpen(currentOpenId, player)) {
        try {
          await player.unload();
        } catch (e) {
          console.warn('[Shaka] stale player unload failed', e);
        }

        try {
          await player.destroy();
        } catch (e) {
          console.warn('[Shaka] stale player destroy failed', e);
        }

        return;
      }

      this.setDefaultAudioTrack();
      onSettingsChange(this.getAvailableSettings());
    } catch (e) {
      if (this.isUnmounted || currentOpenId !== this.openId) return;

      const code = e?.code;
      const codes = shaka.util.Error.Code;
      const codesToIgnore = [codes.LOAD_INTERRUPTED, codes.FAILED_TO_ATTACH_TO_VIDEO, codes.NO_VIDEO_ELEMENT];
      // These errors happen when user scrolls channels fast
      // TODO: Think about code updates, some errors could be prevented by working with player more properly.
      // EG: "unload" and "destroy" are async methods in Shaka instance
      if (codesToIgnore.includes(code)) return;
      // This error happens because video tag render is not in sync with Shaka load/unload
      if (e.name === 'InvalidStateError') return;

      onVideoEvent(VIDEO_EVENTS.ERROR, e.message);
      console.log(e);
    }
  }

  setDefaultAudioTrack() {
    if (!this.shakaVideo) return;

    const { lang } = this.props;
    const availableAudio = this.shakaVideo.getAudioLanguages() || [];

    if (availableAudio.includes('uk')) {
      this.shakaVideo.selectAudioLanguage('uk');
    } else if (lang && availableAudio.includes(lang)) {
      this.shakaVideo.selectAudioLanguage(lang);
    } else if (availableAudio.includes('en')) {
      this.shakaVideo.selectAudioLanguage('en');
    } else if (availableAudio.includes('ru')) {
      this.shakaVideo.selectAudioLanguage('ru');
    }
  }

  getAvailableSettings() {
    // this method could be called async therefore shakaVideo may not exist
    if (!this.shakaVideo) return {};

    const tracks = this.shakaVideo.getVariantTracks();
    const activeTrack = find(tracks, { active: true });

    if (!activeTrack) return {};

    const quality = uniqBy(tracks, 'videoBandwidth').map((track) => ({
      id: track.videoBandwidth,
      isActive: track.videoBandwidth === activeTrack.videoBandwidth,
    }));
    let language = uniqBy(tracks, 'language').map((track) => ({
      id: track.language,
      isActive: track.language === activeTrack.language,
    }));

    const UaTrackIndex = language.findIndex((track) => ['uk', 'ua'].includes(track.id?.toLowerCase()));

    if (UaTrackIndex > -1) {
      language.push(...language.splice(0, UaTrackIndex));
    }

    return { quality, language };
  }

  async destroyPlayer() {
    if (!this.shakaVideo) return;

    const player = this.shakaVideo;
    this.shakaVideo = null;

    try {
      await player.unload();
    } catch (e) {
      console.warn('[Shaka] unload failed', e);
    }

    try {
      await player.destroy();
    } catch (e) {
      console.warn('[Shaka] destroy failed', e);
    }

    const video = this.videoRef.current;

    if (video && this.isUnmounted) {
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch (e) {
        console.warn('[Shaka] video cleanup failed', e);
      }
    }
  }

  getStartPosition() {
    const { startFrom, playerType, contentType } = this.props;

    const isLive =
      playerType === PLAYER_TYPES.CHANNEL || (playerType === PLAYER_TYPES.TIMESHIFT && contentType === 'channel');

    // Для live/channel лучше не передавать 0.
    // Пусть Shaka сама выберет live edge/default start.
    if (isLive) return undefined;

    if (typeof startFrom === 'number' && startFrom > 0) {
      return startFrom;
    }

    return 0;
  }

  render() {
    const { src, width, height, onVideoEvent } = this.props;

    return <VideoComponent width={width} height={height} onEvent={onVideoEvent} ref={this.videoRef} key={src} />;
  }
}

ShakaPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  verimatrixId: PropTypes.string.isRequired,
  onPlayerOpen: PropTypes.func.isRequired,
  onSettingsChange: PropTypes.func.isRequired,
  onVideoEvent: PropTypes.func.isRequired,
  startFrom: PropTypes.number,
  lang: PropTypes.string,
  quality: PropTypes.number,
  forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export default forwardRef((props, ref) => <ShakaPlayer forwardedRef={ref} {...props} />);
